import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Crown, ShieldCheck, ChevronRight, X, Store, Sparkles, CalendarClock } from "lucide-react";
import api from "@/lib/api";

const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`;
const monthName = (m) => new Date(`${m}-02`).toLocaleDateString("es-MX", { month: "long", year: "numeric" });

/** Tarjeta del concurso mensual CUPÓN DE ORO dentro de la Cuponera. */
export default function GoldCard({ deviceId }) {
  const [g, setG] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!deviceId) return;
    api.get(`/pwa/gold/${deviceId}`).then((r) => setG(r.data)).catch(() => {});
  }, [deviceId]);

  if (!g || !g.prize) return null;
  const me = g.me;
  const won = g.last_result?.is_me;

  return (
    <>
      <motion.button initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} onClick={() => setOpen(true)} className="w-full text-left relative overflow-hidden rounded-[28px] p-5 text-[#2b1a00] shadow-2xl shadow-amber-500/30" style={{ background: "linear-gradient(135deg,#fff3b0 0%,#f7c948 35%,#d69e2e 70%,#f6e27a 100%)" }} data-testid="gold-card">
        <Crown className="absolute -right-6 -top-6 w-40 h-40 text-white/25 rotate-12" />
        <div className="relative">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-black"><Trophy className="w-3.5 h-3.5" /> Concurso del mes · {g.month}</div>
          <div className="font-heading font-black text-3xl leading-none mt-1 tracking-tight">CUPÓN DE ORO</div>
          <div className="mt-3 flex gap-3 items-center">
            {g.prize.image ? <img src={g.prize.image} alt="" className="w-16 h-16 rounded-2xl object-cover ring-2 ring-white/60 shrink-0" /> : <div className="w-16 h-16 rounded-2xl bg-white/40 grid place-items-center shrink-0"><Sparkles className="w-7 h-7" /></div>}
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">Premio</div>
              <div className="font-heading font-extrabold text-base leading-tight" data-testid="gold-prize-title">{g.prize.title}</div>
              {g.prize.value_mxn > 0 && <div className="text-xs font-bold mt-0.5">Valor {money(g.prize.value_mxn)} MXN</div>}
            </div>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2 text-center">
            <Mini v={me.rank ? `#${me.rank}` : "—"} l="tu lugar" testid="gold-my-rank" />
            <Mini v={me.merchants} l="comercios" testid="gold-my-merchants" />
            <Mini v={g.days_left} l="días restantes" />
          </div>
          <div className="mt-3 text-xs font-semibold flex items-center justify-between">
            <span>{me.rank === 1 ? "¡Vas en primer lugar! Mantén el ritmo 🔥" : me.rank ? `Usa cupones en más comercios para subir` : "Canjea tu primer cupón para entrar al concurso"}</span>
            <ChevronRight className="w-4 h-4" />
          </div>
          {won && <div className="mt-3 rounded-2xl bg-[#2b1a00] text-[#f7c948] px-3 py-2 text-xs font-extrabold flex items-center gap-2" data-testid="gold-won-banner"><Trophy className="w-4 h-4" /> ¡GANASTE el Cupón de Oro de {monthName(g.last_result.month)}! Toca para ver tu código</div>}
        </div>
      </motion.button>

      <AnimatePresence>{open && <GoldDetail g={g} onClose={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
}

function Mini({ v, l, testid }) {
  return <div className="rounded-2xl bg-white/45 py-2" data-testid={testid}><div className="font-heading font-black text-lg leading-none">{v}</div><div className="text-[9px] uppercase tracking-wider font-bold opacity-70 mt-1">{l}</div></div>;
}

function GoldDetail({ g, onClose }) {
  const last = g.last_result;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm overflow-y-auto" onClick={onClose} data-testid="gold-detail">
      <div className="min-h-full flex items-end sm:items-center justify-center p-4">
        <motion.div initial={{ y: 60 }} animate={{ y: 0 }} exit={{ y: 60 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-[28px] overflow-hidden bg-[#141414] text-white">
          <div className="p-5 text-[#2b1a00]" style={{ background: "linear-gradient(135deg,#fff3b0,#f7c948 45%,#d69e2e)" }}>
            <div className="flex items-start justify-between">
              <div><div className="text-[10px] uppercase tracking-[0.25em] font-black">Cupón de Oro · {monthName(g.month)}</div><div className="font-heading font-black text-2xl leading-tight mt-1">{g.prize.title}</div></div>
              <button onClick={onClose} className="w-8 h-8 rounded-full bg-black/10 grid place-items-center" data-testid="gold-detail-close"><X className="w-4 h-4" /></button>
            </div>
            {g.prize.image && <img src={g.prize.image} alt="" className="mt-3 w-full aspect-[16/9] object-cover rounded-2xl ring-2 ring-white/60" />}
            {g.prize.description && <p className="text-sm mt-3 leading-snug">{g.prize.description}</p>}
            {g.prize.sponsor && <div className="text-[10px] font-bold mt-2 opacity-70">Patrocina: {g.prize.sponsor}</div>}
          </div>

          <div className="p-5 space-y-5">
            {last?.is_me && (
              <div className="rounded-2xl border-2 border-[#f7c948] p-4 text-center" data-testid="gold-my-code">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#f7c948]">¡Ganaste {monthName(last.month)}!</div>
                <div className="font-mono font-black text-2xl tracking-[0.2em] mt-1">{last.code}</div>
                <p className="text-xs text-white/70 mt-1">Premio: {last.prize_title}. {last.status === "delivered" ? "Ya fue entregado 🎉" : "Presenta este código para reclamarlo — te contactaremos por la app."}</p>
              </div>
            )}

            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-white/50 mb-2 flex items-center gap-1"><Trophy className="w-3 h-3" /> Tabla del mes · {g.participants} participante{g.participants === 1 ? "" : "s"}</div>
              {g.leaderboard.length === 0 ? <div className="text-sm text-white/60">Aún nadie ha canjeado este mes. ¡Sé el primero!</div> : (
                <div className="space-y-1.5" data-testid="gold-leaderboard">
                  {g.leaderboard.map((r) => (
                    <div key={r.device_id} className={`flex items-center gap-3 rounded-xl px-3 py-2 ${r.rank === 1 ? "bg-[#f7c948]/15 border border-[#f7c948]/40" : "bg-white/5"}`}>
                      <div className={`w-7 h-7 rounded-full grid place-items-center font-black text-xs ${r.rank === 1 ? "bg-[#f7c948] text-black" : "bg-white/10"}`}>{r.rank}</div>
                      <div className="flex-1 font-bold text-sm truncate">{r.display}</div>
                      <div className="text-xs text-white/70 flex items-center gap-1"><Store className="w-3 h-3" /> {r.merchants} · {r.redemptions} cupones</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-white/50 mb-2 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Reglas</div>
              <ul className="text-xs text-white/75 space-y-1.5 list-disc list-inside">{g.rules.map((r) => <li key={r}>{r}</li>)}</ul>
              {g.me.flagged > 0 && <p className="mt-2 text-[11px] text-amber-300" data-testid="gold-flagged-note">⚠️ {g.me.flagged} de tus canjes está en revisión y por ahora no suma al concurso.</p>}
            </div>

            {last && !last.is_me && last.winner_display && (
              <div className="rounded-2xl bg-white/5 p-3 text-xs text-white/70 flex items-center gap-2"><CalendarClock className="w-4 h-4 text-[#f7c948]" /> Ganador de {monthName(last.month)}: <strong className="text-white">{last.winner_display}</strong> con {last.score?.merchants} comercios.</div>
            )}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
